/** @jsx jsx */

/** @jsxFrag */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { createContext, useState } from 'react';

import { PageGridContainer, PageNotification, PageNotificationList, breakpoints } from '@coursera/cds-core';
import type { PageNotificationProps } from '@coursera/cds-core';

export type ProfileNotificationInputType = {
  title?: string;
  severity?: PageNotificationProps['severity'];
  message: string;
};

export type ProfileNotificationType = ProfileNotificationInputType & {
  id: string;
};

export type ProfileNotificationContextType = {
  sendNotification: (notification: ProfileNotificationInputType) => void;
};

type Props = {
  children: React.ReactElement;
};

const NUM_OF_DISPLAY_ITEMS = 3;

export const ProfileNotificationContext = createContext<ProfileNotificationContextType>({
  sendNotification: () => undefined,
});

const styles = {
  pageGridContainer: () => css`
    ${breakpoints.down('xs')} {
      padding: 0;
    }
  `,
  notificationList: () => css`
    width: 100%;

    ul > li {
      padding-top: var(--cds-spacing-200);
    }
  `,
  withPaddingX: () => css`
    padding: 0 var(--cds-spacing-600);

    ${breakpoints.between('xs', 'sm')} {
      padding: 0 var(--cds-spacing-300);
    }

    ${breakpoints.down('xs')} {
      padding: 0;
    }
  `,
  withPaddingTop: () => css`
    padding-top: var(--cds-spacing-200);
  `,
  withPositionSticky: css`
    position: sticky;
    z-index: 10001;
    top: 65px;
  `,
};

const generateId = () => Math.random().toString(36).substring(2);

export const ProfileNotificationsProvider = ({ children }: Props) => {
  const [notifications, setNotifications] = useState<Array<ProfileNotificationType>>([]);

  const sendNotification = (notification: ProfileNotificationInputType) => {
    setNotifications((prev) => [...prev, { ...notification, id: generateId() }]);
  };

  const dismissNotification = (notification: ProfileNotificationType) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
  };

  return (
    <ProfileNotificationContext.Provider value={{ sendNotification }}>
      {notifications.length > 0 && (
        <div css={[styles.withPaddingTop, styles.withPositionSticky]}>
          <PageGridContainer css={styles.pageGridContainer}>
            <PageNotificationList css={[styles.notificationList, styles.withPaddingX]}>
              {notifications.slice(0 - NUM_OF_DISPLAY_ITEMS).map((notification) => (
                <PageNotification
                  key={notification.id}
                  title={notification.title}
                  severity={notification.severity}
                  onDismiss={() => dismissNotification(notification)}
                >
                  {notification.message}
                </PageNotification>
              ))}
            </PageNotificationList>
          </PageGridContainer>
        </div>
      )}
      {children}
    </ProfileNotificationContext.Provider>
  );
};
